/**
 * CR508.1 / CR523E: S1.4 Prompt Pack Selector
 * Queries prompt_packs (pack_kind=WORKFLOW, usage_stage=S1_4_LOCATOR)
 * Defaults to app_scan_settings.s1_4_locator_prompt_pack_id
 * CR523E: Validates that selected pack has items with stage=LOCATE_SNIPPET
 */
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Package, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PackOption {
  id: string;
  name: string;
  version: number;
  is_active: boolean;
  latest_snapshot_version?: number | null;
}

interface S14PromptPackSelectorProps {
  selectedSnapshotId: string | null;
  onSelect: (snapshotId: string | null) => void;
}

export function S14PromptPackSelector({ selectedSnapshotId, onSelect }: S14PromptPackSelectorProps) {
  const [options, setOptions] = useState<PackOption[]>([]);
  const [loading, setLoading] = useState(true);
  // CR523E: Stage readiness state
  const [stageReady, setStageReady] = useState<boolean | null>(null);

  useEffect(() => {
    async function fetchOptions() {
      setLoading(true);

      // Parallel: fetch WORKFLOW packs for S1_4_LOCATOR + scan settings default
      const [packsResult, settingsResult] = await Promise.all([
        supabase
          .from("prompt_packs")
          .select("id, name, version, is_active")
          .eq("pack_kind", "WORKFLOW")
          .eq("usage_stage", "S1_4_LOCATOR")
          .is("archived_at", null)
          .order("is_active", { ascending: false })
          .order("name", { ascending: true }),
        supabase
          .from("app_scan_settings")
          .select("s1_4_locator_prompt_pack_id")
          .eq("singleton_id", true)
          .maybeSingle(),
      ]);

      const packs = (packsResult.data ?? []) as PackOption[];
      setOptions(packs);

      // Auto-select: use scan settings default, else first active, else first
      if (!selectedSnapshotId && packs.length > 0) {
        const defaultId = (settingsResult.data as any)?.s1_4_locator_prompt_pack_id;
        const match = defaultId ? packs.find(p => p.id === defaultId) : null;
        const pick = match || packs.find(p => p.is_active) || packs[0];
        onSelect(pick.id);
      }

      setLoading(false);
    }
    fetchOptions();
  }, []);

  // CR523E: Validate stage readiness when pack selection changes
  useEffect(() => {
    if (!selectedSnapshotId) {
      setStageReady(null);
      return;
    }

    async function checkStageReadiness() {
      const { data: items } = await supabase
        .from("prompt_pack_items")
        .select("stage")
        .eq("prompt_pack_id", selectedSnapshotId!)
        .eq("enabled", true);

      if (!items) {
        setStageReady(false);
        return;
      }

      const hasCropStage = items.some((item: any) => item.stage === "LOCATE_SNIPPET");
      setStageReady(hasCropStage);
    }
    checkStageReadiness();
  }, [selectedSnapshotId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Package className="h-3.5 w-3.5" />
        <span className="text-xs">Loading packs...</span>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5" />
          Locator pack
        </Label>
        <span className="text-xs text-muted-foreground">No S1.5 Locator packs found</span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground flex items-center gap-1.5 whitespace-nowrap">
          <Package className="h-3.5 w-3.5" />
          Locator pack
        </Label>
        <Select
          value={selectedSnapshotId || ""}
          onValueChange={(v) => onSelect(v || null)}
        >
          <SelectTrigger className="h-7 text-xs w-[260px]">
            <SelectValue placeholder="Select pack..." />
          </SelectTrigger>
          <SelectContent>
            {options.map(opt => (
              <SelectItem key={opt.id} value={opt.id} className="text-xs">
                <div className="flex items-center gap-2">
                  <span>{opt.name}</span>
                  <span className="text-muted-foreground">published v{opt.version}</span>
                  {opt.is_active && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0">Active</Badge>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* CR523E: Stage readiness indicator */}
      {selectedSnapshotId && stageReady !== null && (
        <div className="flex items-center gap-1.5 ml-5 text-[11px]">
          {stageReady ? (
            <>
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span className="text-muted-foreground">has LOCATE_SNIPPET stage</span>
            </>
          ) : (
            <>
              <XCircle className="h-3 w-3 text-destructive" />
              <span className="text-destructive">missing LOCATE_SNIPPET stage — will use deterministic mode</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
